/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Box, Button, Flex, IconChevronDown, IconError, IconLoading, Text } from "@powerledger/ui-component-lib";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { areDocumentsUploaded } from "@/app/lib/account-service-helpers";

import { DocUpload } from "./doc-upload";
import { DocumentBlock } from "./document-block";
import { DocumentUploadBlockSeparatorProps, RequiredDocumentsViewProps } from "./required-documents.types";
import { documentTypeLabelMap, getContactName } from "./use-required-documents";

export const RequiredDocumentsView: FC<RequiredDocumentsViewProps> = ({
  beneficialOwners,
  setIsUSPerson,
  selectedRequiredDocument,
  setSelectedRequiredDocument,
  requiredCompanyDocuments,
  noEdit,
  isUSPerson,
  showStatusText,
  company,
  requiredControllerDocuments,
  controller,
  hasBeneficialOwners,
  uploadBlocksToggleState,
  loading,
  toggleUploadBlocksState,
  disableReUpload,
}) => {
  const { t } = useTranslation();
  const renderBeneficialOwnersDocs = useMemo(
    () =>
      beneficialOwners?.map((beneficialOwnerEntity) => {
        return beneficialOwnerEntity?.documents?.map((requiredBeneficialOwnerDocument) => (
          <DocumentBlock
            noEdit={noEdit || disableReUpload.includes(requiredBeneficialOwnerDocument.id)}
            key={requiredBeneficialOwnerDocument.id}
            onUpload={() => {
              setIsUSPerson(!!beneficialOwnerEntity?.identityNumber);
              setSelectedRequiredDocument(requiredBeneficialOwnerDocument);
            }}
            documentOf={beneficialOwnerEntity}
            titlePrefix={`Beneficial Owner ${getContactName(beneficialOwnerEntity)}`}
            requiredDocument={requiredBeneficialOwnerDocument}
            showStatusText={showStatusText}
          />
        ));
      }),
    [beneficialOwners, disableReUpload, setSelectedRequiredDocument, noEdit, setIsUSPerson, showStatusText],
  );
  const renderCompanyControllerDocs = useMemo(
    () => (
      <>
        {requiredCompanyDocuments.map((requiredCompanyDocument) => (
          <DocumentBlock
            noEdit={noEdit || disableReUpload.includes(requiredCompanyDocument.id)}
            key={requiredCompanyDocument.id}
            titlePrefix={`Company`}
            onUpload={() => {
              setIsUSPerson(false);
              setSelectedRequiredDocument(requiredCompanyDocument);
            }}
            documentOf={company}
            requiredDocument={requiredCompanyDocument}
            showStatusText={showStatusText}
          />
        ))}
        {requiredControllerDocuments.map((requiredControllerDocument, index) => (
          <DocumentBlock
            noEdit={noEdit || disableReUpload.includes(requiredControllerDocument.id)}
            key={requiredControllerDocument?.id || index}
            onUpload={() => {
              setIsUSPerson(!!controller?.identityNumber);
              setSelectedRequiredDocument(requiredControllerDocument);
            }}
            documentOf={controller}
            titlePrefix={`Controller ${getContactName(controller)}`}
            requiredDocument={requiredControllerDocument}
            showStatusText={showStatusText}
          />
        ))}
      </>
    ),
    [
      company,
      controller,
      requiredCompanyDocuments,
      requiredControllerDocuments,
      disableReUpload,
      noEdit,
      setIsUSPerson,
      setSelectedRequiredDocument,
      showStatusText,
    ],
  );

  if (loading) return <IconLoading />;

  if (selectedRequiredDocument)
    return (
      <DocUpload
        onFinish={() => {
          setSelectedRequiredDocument(undefined);
        }}
        requiredDocument={selectedRequiredDocument}
        isUSPerson={isUSPerson}
      />
    );

  return !noEdit ? (
    <>
      <DocumentUploadBlocksSeparator
        toggleState={uploadBlocksToggleState.companyControllerBlock}
        type="companyControllerBlock"
        onToggle={toggleUploadBlocksState("companyControllerBlock")}
        showError={
          !areDocumentsUploaded(requiredCompanyDocuments) || !areDocumentsUploaded(requiredControllerDocuments)
        }
      >
        {!requiredCompanyDocuments?.length && !requiredControllerDocuments?.length && (
          <Text
            sx={{
              color: "textDarker",
              margin: "auto",
              my: 2,
            }}
          >
            {t("No Company or Controller Added Yet")}
          </Text>
        )}
        {renderCompanyControllerDocs}
      </DocumentUploadBlocksSeparator>
      <DocumentUploadBlocksSeparator
        toggleState={uploadBlocksToggleState.beneficialOwnerBlock}
        type="beneficialOwnerBlock"
        onToggle={toggleUploadBlocksState("beneficialOwnerBlock")}
        showError={
          hasBeneficialOwners &&
          !beneficialOwners?.every((beneficialOwnerEntity) => areDocumentsUploaded(beneficialOwnerEntity.documents))
        }
      >
        {!hasBeneficialOwners && (
          <Text
            sx={{
              color: "textDarker",
              margin: "auto",
              my: 2,
            }}
          >
            {t("No Beneficial Owners Added Yet")}
          </Text>
        )}
        {renderBeneficialOwnersDocs}
      </DocumentUploadBlocksSeparator>
    </>
  ) : (
    <Flex
      sx={{
        pt: 2,
        px: 2,
        justifyContent: "center",
        flexWrap: "wrap",
        mx: -2,
      }}
    >
      {renderCompanyControllerDocs}
      {renderBeneficialOwnersDocs}
    </Flex>
  );
};

const DocumentUploadBlocksSeparator: FC<DocumentUploadBlockSeparatorProps> = ({
  toggleState,
  type,
  onToggle,
  showError,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        mt: 2,
        cursor: "pointer",
        borderWidth: "1px",
        borderStyle: "solid",
        maxHeight: toggleState ? "1000px" : "60px",
        borderColor: "text",
        overflowY: "hidden",
        overflowX: "hidden",
        transition: "all 0.3s ease-in-out",
      }}
    >
      <Button
        sx={{
          width: "100%",
          background: "transparent",
          "&:enabled:hover, &:enabled:focus, &:enabled:active": {
            background: "transparent",
          },
          borderRadius: 0,
          borderBottom: "1px solid",
          borderColor: "primary",
          p: 3,
          color: "textDarker",
          justifyContent: "space-between",
          height: "60px",
        }}
        onClick={() => onToggle()}
        data-testid={`required-documents--${type}`}
      >
        <Flex sx={{ gap: 1, alignItems: "center" }}>
          {showError && <IconError size={3} noSvgPositioning color="error.700" />}
          {t(documentTypeLabelMap[type])}
        </Flex>
        <Box
          sx={{
            svg: {
              transition: "all 0.3s ease-in-out",
              transform: toggleState ? "rotate(180deg)" : "",
              path: {
                fill: "success.500",
              },
            },
          }}
        >
          <IconChevronDown size={6} />
        </Box>
      </Button>
      <Flex
        sx={{
          pt: 2,
          px: 2,
          justifyContent: "center",
          flexWrap: "wrap",
          mx: -2,
        }}
      >
        {children}
      </Flex>
    </Box>
  );
};
